import React, { useState, useEffect } from 'react';
import { Receipt, Plus, Search, Filter, Home, Building, PiggyBank, CreditCard, Globe, Calculator, TrendingUp, FileText, DollarSign, BarChart3, Calendar, Edit, Trash2, Eye, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import BackToTaxFormsBanner from '../components/BackToTaxFormsBanner';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import toast from 'react-hot-toast';
import CSVImport from '../components/CSVImport';
import BankStatementAnalyzer from '../components/BankStatementAnalyzer';
import TaxYearTracker from '../components/TaxYearTracker';
import ValidatedInput from '../components/ValidatedInput';
import ValidatedSelect from '../components/ValidatedSelect';
import ValidatedTextarea from '../components/ValidatedTextarea';
import { validationRules } from '../utils/validation';
import { getTaxYearOptions } from '../utils/taxYears';
import Disclaimer from '../components/Disclaimer';
import '../components/ValidatedInput.css';

const ExpenseTracker = () => {
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showEditExpense, setShowEditExpense] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    property_id: null,
    category: '',
    tax_year: '', // '' = All Years (shows imported bank statement expenses from any year)
    is_deductible: '',
    is_deductible: '',
    expense_type: '',
    start_date: '',
    end_date: '',
    min_amount: '',
    max_amount: ''
  });

  // Recurring expenses state
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [monthlyRentIncome, setMonthlyRentIncome] = useState(0);
  const [showAddRecurring, setShowAddRecurring] = useState(false);
  const [showEditRecurring, setShowEditRecurring] = useState(false);
  const [selectedRecurring, setSelectedRecurring] = useState(null);
  const [activeTab, setActiveTab] = useState('expenses'); // 'expenses', 'recurring', 'statements', 'tax-year', or 'cash-flow'
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showBankAnalyzer, setShowBankAnalyzer] = useState(false);
  
  // Monthly income from imported transactions (taxable_income_transactions, incl. primary-home)
  const [monthlyIncomeFromTransactions, setMonthlyIncomeFromTransactions] = useState({});

  // Cash flow state
  const [cashFlowData, setCashFlowData] = useState({
    monthlyIncome: {},
    monthlyExpenses: {},
    monthlySavings: {},
    selectedMonth: new Date().toISOString().slice(0, 7), // YYYY-MM format
    selectedYear: new Date().getFullYear() // Used by Cash Flow Dashboard year dropdown
  });

  // Form hook for add expense form
  const { register: registerExpense, handleSubmit: handleSubmitExpense, formState: { errors: expenseErrors }, reset: resetExpense, watch: watchExpense } = useForm({
    defaultValues: {
      description: '',
      amount: '',
      category: '',
      property_id: '',
      account_id: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      receipt_url: '',
      is_deductible: true,
      tax_year: new Date().getFullYear(),
      expense_type: 'business',
      payment_method: ''
    }
  });

  const watchedPropertyId = watchExpense('property_id');

  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: '',
    category: '',
    property_id: '',
    account_id: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    receipt_url: '',
    is_deductible: true,
    tax_year: new Date().getFullYear(),
    expense_type: 'business',
    payment_method: ''
  });

  const [newRecurringExpense, setNewRecurringExpense] = useState({
    description: '',
    amount: '',
    category: '',
    property_id: null,
    frequency: 'monthly',
    date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0], // Due date for this bill
    bill_type: '',
    notes: '',
    is_deductible: true,
    auto_pay: false,
    tax_year: new Date().getFullYear(),
    expense_type: 'business'
  });

  // Enhanced expense categories with rental-specific options
  const expenseCategories = {
    property_taxes: { name: 'Property Taxes', icon: '🏛️', deductible: true },
    insurance: { name: 'Insurance', icon: '🛡️', deductible: true },
    maintenance: { name: 'Maintenance', icon: '🔧', deductible: true },
    utilities: { name: 'Utilities', icon: '⚡', deductible: true },
    mortgage: { name: 'Mortgage Interest', icon: '🏠', deductible: true },
    repairs: { name: 'Repairs', icon: '🔨', deductible: true },
    landscaping: { name: 'Landscaping', icon: '🌱', deductible: true },
    hoa_dues: { name: 'HOA Dues', icon: '🏘️', deductible: true },
    property_management: { name: 'Property Management', icon: '👥', deductible: true },
    tenant_screening: { name: 'Tenant Screening', icon: '🔍', deductible: true },
    legal_fees: { name: 'Legal Fees', icon: '⚖️', deductible: true },
    appliance_replacement: { name: 'Appliance Replacement', icon: '📺', deductible: true },
    structural_repairs: { name: 'Structural Repairs', icon: '🏗️', deductible: true },
    emergency_repairs: { name: 'Emergency Repairs', icon: '🚨', deductible: true },
    hvac_maintenance: { name: 'HVAC Maintenance', icon: '❄️', deductible: true },
    snow_removal: { name: 'Snow Removal', icon: '❄️', deductible: true },
    seasonal_landscaping: { name: 'Seasonal Landscaping', icon: '🍂', deductible: true },
    other: { name: 'Other', icon: '📝', deductible: true },
    travel: { name: 'Travel', icon: '✈️', deductible: true },
    office_supplies: { name: 'Office Supplies', icon: '📎', deductible: true },
    professional_services: { name: 'Professional Services', icon: '👨‍💼', deductible: true }
  };

  // Helper function to get alphabetized expense categories
  const getAlphabetizedCategories = () => {
    return Object.entries(expenseCategories).sort((a, b) => {
      return a[1].name.localeCompare(b[1].name);
    });
  };

  // Bill types for recurring expenses
  const billTypes = {
    mortgage: { name: 'Mortgage Payment', icon: '🏠' },
    insurance: { name: 'Insurance', icon: '🛡️' },
    utilities: { name: 'Utilities', icon: '⚡' },
    property_taxes: { name: 'Property Taxes', icon: '🏛️' },
    maintenance: { name: 'Maintenance', icon: '🔧' },
    hoa: { name: 'HOA Dues', icon: '🏘️' },
    property_management: { name: 'Property Management', icon: '👥' },
    landscaping: { name: 'Landscaping', icon: '🌱' },
    hvac: { name: 'HVAC Service', icon: '❄️' },
    other: { name: 'Other', icon: '💰' }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch expenses when filters change
  useEffect(() => {
    fetchExpenses();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch properties, accounts, expense summary, recurring expenses, and monthly income from transactions
      // Use allSettled so one failing request (e.g. income 404) doesn't block the rest
      const currentYear = new Date().getFullYear();
      const auth = { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } };
      const results = await Promise.allSettled([
        axios.get('/api/properties', auth),
        axios.get('/api/accounts', auth),
        axios.get(`/api/expenses/summary?tax_year=${filters.tax_year}`, auth),
        axios.get('/api/expenses/recurring', auth),
        axios.get(`/api/income/monthly-summary?year=${currentYear}`, auth),
        axios.get(`/api/income/monthly-summary?year=${currentYear - 1}`, auth)
      ]);

      const [propertiesRes, accountsRes, summaryRes, recurringRes, incomeCurRes, incomePrevRes] = results.map(r =>
        r.status === 'fulfilled' ? r.value : { data: { success: false } }
      );

      if (propertiesRes.data?.success) {
        setProperties(propertiesRes.data.data);
        const totalRent = (propertiesRes.data.data || [])
          .filter(p => p.monthly_rent)
          .reduce((sum, p) => sum + (parseFloat(p.monthly_rent || 0)), 0);
        setMonthlyRentIncome(totalRent);
      }
      if (accountsRes.data?.success) setAccounts(accountsRes.data.data);
      if (summaryRes.data?.success) setExpenseSummary(summaryRes.data.data);
      if (recurringRes.data?.success) setRecurringExpenses(recurringRes.data.data);
      const incomeCur = incomeCurRes.data?.success ? incomeCurRes.data.data || {} : {};
      const incomePrev = incomePrevRes.data?.success ? incomePrevRes.data.data || {} : {};
      setMonthlyIncomeFromTransactions({ ...incomePrev, ...incomeCur });
      
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status !== 500) {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await axios.get('/api/expenses', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
        params: filters
      });
      
      if (response.data.success) {
        setExpenses(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      // Don't show error toast for expenses - it's normal to have no expenses yet
      if (error.response?.status !== 500) {
        toast.error('Failed to fetch expenses');
      }
      setExpenses([]);
    }
  };

  const fetchRecurringExpenses = async () => {
    try {
      const response = await axios.get('/api/expenses/recurring', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
      });
      
      if (response.data.success) {
        setRecurringExpenses(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching recurring expenses:', error);
      setRecurringExpenses([]);
    }
  };

  const handleAddExpense = async (data) => {
    try {
      // Auto-set tax deductible based on property type
      const selectedProperty = properties.find(p => p.id === parseInt(data.property_id));
      const isDeductible = selectedProperty?.type === 'rental' ? true : (data.is_deductible || false);
      
      const expenseData = {
        ...data,
        property_id: data.property_id ? parseInt(data.property_id) : null,
        account_id: data.account_id ? parseInt(data.account_id) : null,
        amount: parseFloat(data.amount) || 0,
        tax_year: parseInt(data.tax_year) || new Date().getFullYear(),
        is_deductible: isDeductible
      };

      const response = await axios.post('/api/expenses', expenseData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
      });
      
      if (response.data.success) {
        toast.success('Expense added successfully');
        setShowAddExpense(false);
        resetExpense();
        setNewExpense({
          description: '',
          amount: '',
          category: '',
          property_id: '',
          account_id: '',
          date: new Date().toISOString().split('T')[0],
          notes: '',
          receipt_url: '',
          is_deductible: true,
          tax_year: new Date().getFullYear(),
          expense_type: 'business',
          payment_method: ''
        });
        fetchExpenses();
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    }
  };

  const handleEditExpense = async () => {
    try {
      // Ensure date is in correct format
      const expenseData = {
        ...selectedExpense,
        date: selectedExpense.date ? new Date(selectedExpense.date).toISOString() : new Date().toISOString()
      };
      
      const response = await axios.put(`/api/expenses/${selectedExpense.id}`, expenseData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
      });
      
      if (response.data.success) {
        toast.success('Expense updated successfully');
        setShowEditExpense(false);
        setSelectedExpense(null);
        fetchExpenses();
      }
    } catch (error) {
      console.error('Error updating expense:', error);
      if (error.response && error.response.data && error.response.data.details) {
        toast.error(`Validation error: ${error.response.data.details.join(', ')}`);
      } else {
        toast.error('Failed to update expense');
      }
    }
  };

  const handleDeleteExpense = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        const response = await axios.delete(`/api/expenses/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
        });
        
        if (response.data.success) {
          toast.success('Expense deleted successfully');
          fetchExpenses();
        }
      } catch (error) {
        console.error('Error deleting expense:', error);
        toast.error('Failed to delete expense');
      }
    }
  };

  // Recurring expense handlers
  const handleAddRecurringExpense = async () => {
    try {
      // Basic validation
      if (!newRecurringExpense.description || !newRecurringExpense.amount || !newRecurringExpense.category) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      // Check frequency specifically
      if (!newRecurringExpense.frequency || newRecurringExpense.frequency === '') {
        toast.error('Please select a frequency');
        return;
      }
      
      // Ensure date is in correct format and clean up the data
      const expenseData = {
        description: newRecurringExpense.description,
        amount: parseFloat(newRecurringExpense.amount) || 0,
        category: newRecurringExpense.category,
        date: newRecurringExpense.date ? new Date(newRecurringExpense.date).toISOString() : new Date().toISOString(),
        property_id: newRecurringExpense.property_id || null,
        frequency: newRecurringExpense.frequency || 'monthly',
        due_date: newRecurringExpense.due_date ? new Date(newRecurringExpense.due_date).toISOString() : null,
        bill_type: newRecurringExpense.bill_type || null,
        notes: newRecurringExpense.notes || null,
        is_deductible: newRecurringExpense.is_deductible,
        auto_pay: newRecurringExpense.auto_pay,
        tax_year: newRecurringExpense.tax_year,
        expense_type: newRecurringExpense.expense_type
      };
      
      console.log('🔍 Original newRecurringExpense state:', newRecurringExpense);
      console.log('🔍 Sending recurring expense data:', expenseData);
      console.log('🔍 Frequency value:', expenseData.frequency, 'Type:', typeof expenseData.frequency);
      
      const response = await axios.post('/api/expenses/recurring', expenseData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
      });
      
      if (response.data.success) {
        toast.success('Recurring expense added successfully');
        setShowAddRecurring(false);
        setNewRecurringExpense({
          description: '',
          amount: '',
          category: '',
          property_id: null,
          frequency: 'monthly',
          date: new Date().toISOString().split('T')[0],
          due_date: new Date().toISOString().split('T')[0],
          bill_type: '',
          notes: '',
          is_deductible: true,
          auto_pay: false,
          tax_year: new Date().getFullYear(),
          expense_type: 'business'
        });
        // Refresh recurring expenses
        fetchRecurringExpenses();
      }
    } catch (error) {
      console.error('Error adding recurring expense:', error);
      if (error.response && error.response.data && error.response.data.details) {
        toast.error(`Validation error: ${error.response.data.details.join(', ')}`);
      } else {
        toast.error('Failed to add recurring expense');
      }
    }
  };

  const handleEditRecurringExpense = async () => {
    try {
      // Ensure date is in correct format and clean up the data
      let formattedDate = new Date().toISOString(); // Default to current date
      if (selectedRecurring.date) {
        const dateObj = new Date(selectedRecurring.date);
        if (!isNaN(dateObj.getTime())) {
          formattedDate = dateObj.toISOString();
        }
      }
      
      // Remove fields that shouldn't be sent to the server
      const { id, user_id, created_at, updated_at, ...recurringData } = selectedRecurring;
      
      const expenseData = {
        ...recurringData,
        date: formattedDate,
        property_id: selectedRecurring.property_id || null,
        amount: parseFloat(selectedRecurring.amount) || 0
      };
      
      console.log('🔍 Updating recurring expense with data:', expenseData);
      
      const response = await axios.put(`/api/expenses/recurring/${id}`, expenseData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
      });
      
      if (response.data.success) {
        toast.success('Recurring expense updated successfully');
        setShowEditRecurring(false);
        setSelectedRecurring(null);
        // Refresh recurring expenses
        fetchRecurringExpenses();
      }
    } catch (error) {
      console.error('Error updating recurring expense:', error);
      if (error.response && error.response.data && error.response.data.details) {
        toast.error(`Validation error: ${error.response.data.details.join(', ')}`);
      } else {
        toast.error('Failed to update recurring expense');
      }
    }
  };

  const handleDeleteRecurringExpense = async (id) => {
    if (window.confirm('Are you sure you want to delete this recurring expense?')) {
      try {
        const response = await axios.delete(`/api/expenses/recurring/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
        });
        
        if (response.data.success) {
          toast.success('Recurring expense deleted successfully');
          // Refresh recurring expenses
          fetchRecurringExpenses();
        }
      } catch (error) {
        console.error('Error deleting recurring expense:', error);
        toast.error('Failed to delete recurring expense');
      }
    }
  };

  const toDateInputValue = (value) => {
    if (!value && value !== 0) return new Date().toISOString().split('T')[0];
    try {
      // Handle numbers or numeric strings (including "1756598400000.0")
      if (typeof value === 'number') {
        return new Date(value).toISOString().split('T')[0];
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        // If already YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
        // If numeric-like string (possibly with .0)
        if (/^\d+(\.\d+)?$/.test(trimmed)) {
          const ms = Math.floor(parseFloat(trimmed));
          return new Date(ms).toISOString().split('T')[0];
        }
        // Otherwise let Date parse (ISO, etc.)
        const d = new Date(trimmed);
        if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
      }
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    } catch (_) {}
    // Fallback to today
    return new Date().toISOString().split('T')[0];
  };

  const openEditExpense = (expense) => {
    const expenseData = {
      ...expense,
      date: toDateInputValue(expense.date)
    };
    setSelectedExpense(expenseData);
    setShowEditExpense(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.name : 'N/A';
  };

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    return account ? account.name : 'N/A';
  };

  // Recurring expenses calculations
  const calculateMonthlyExpenses = () => {
    let total = 0;
    recurringExpenses.forEach(expense => {
      if (expense.frequency === 'monthly') {
        total += expense.amount;
      } else if (expense.frequency === 'quarterly') {
        total += expense.amount / 3; // Monthly portion
      } else if (expense.frequency === 'semi_annually') {
        total += expense.amount / 6; // Monthly portion
      } else if (expense.frequency === 'yearly') {
        total += expense.amount / 12; // Monthly portion
      }
    });
    return total;
  };

  const calculateMonthlySavings = () => {
    let total = 0;
    recurringExpenses.forEach(expense => {
      if (expense.frequency === 'quarterly') {
        total += expense.amount / 3; // Monthly savings needed
      } else if (expense.frequency === 'semi_annually') {
        total += expense.amount / 6; // Monthly savings needed
      } else if (expense.frequency === 'yearly') {
        total += expense.amount / 12; // Monthly savings needed
      }
    });
    return total;
  };

  const calculateNetMonthlyCashFlow = () => {
    return monthlyRentIncome - calculateMonthlyExpenses();
  };

  // Comprehensive cash flow calculations
  const calculateMonthlyIncome = (year, month) => {
    let total = 0;
    
    // Income from properties (monthly_rent - includes primary homes for cash flow)
    properties.forEach(property => {
      if (property.monthly_rent) {
        total += parseFloat(property.monthly_rent) || 0;
      }
    });
    
    // Add income from imported transactions (taxable_income_transactions - includes primary-home/non-taxable)
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;
    total += parseFloat(monthlyIncomeFromTransactions[monthKey]) || 0;
    
    // Add other income sources (from expenses categorized as rental_income - legacy)
    const monthExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate.getFullYear() === year && 
             expenseDate.getMonth() === month - 1 &&
             expense.category === 'rental_income';
    });
    
    monthExpenses.forEach(expense => {
      total += parseFloat(expense.amount) || 0;
    });
    
    return total;
  };

  const calculateMonthlyExpensesByCategory = (year, month) => {
    const categories = {};
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;
    
    const monthExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate.getFullYear() === year && 
             expenseDate.getMonth() === month - 1 &&
             expense.category !== 'rental_income' && // Exclude income
             expense.category !== 'savings'; // Exclude savings
    });
    
    monthExpenses.forEach(expense => {
      const category = expense.category || 'other';
      categories[category] = (categories[category] || 0) + (parseFloat(expense.amount) || 0);
    });
    
    // Add recurring expenses for the month (skip recurring that have individual transactions - already counted above)
    const normalizeDesc = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const regularDescAmounts = new Set(
      expenses.filter(e => !e.is_recurring).map(e => `${normalizeDesc(e.description)}|${e.amount}`)
    );
    recurringExpenses.forEach(expense => {
      if (regularDescAmounts.has(`${normalizeDesc(expense.description)}|${expense.amount}`)) return;
      if (expense.frequency === 'monthly') {
        categories[expense.category || 'recurring'] = (categories[expense.category || 'recurring'] || 0) + (parseFloat(expense.amount) || 0);
      } else if (expense.frequency === 'quarterly') {
        categories[expense.category || 'recurring'] = (categories[expense.category || 'recurring'] || 0) + ((parseFloat(expense.amount) || 0) / 3);
      } else if (expense.frequency === 'semi_annually') {
        categories[expense.category || 'recurring'] = (categories[expense.category || 'recurring'] || 0) + ((parseFloat(expense.amount) || 0) / 6);
      } else if (expense.frequency === 'yearly') {
        categories[expense.category || 'recurring'] = (categories[expense.category || 'recurring'] || 0) + ((parseFloat(expense.amount) || 0) / 12);
      }
    });
    
    return categories;
  };

  const calculateMonthlySavingsByMonth = (year, month) => {
    let total = 0;
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;
    
    // Savings from categorized transactions
    const monthExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate.getFullYear() === year && 
             expenseDate.getMonth() === month - 1 &&
             expense.category === 'savings';
    });
    
    monthExpenses.forEach(expense => {
      total += parseFloat(expense.amount) || 0;
    });
    
    return total;
  };

  const calculateNetCashFlow = (year, month) => {
    const income = calculateMonthlyIncome(year, month);
    const expenses = Object.values(calculateMonthlyExpensesByCategory(year, month)).reduce((sum, amount) => sum + amount, 0);
    const savings = calculateMonthlySavingsByMonth(year, month);
    
    return {
      income,
      expenses,
      savings,
      netFlow: income - expenses - savings
    };
  };

  const getMonthlyTrends = (months = 12) => {
    const trends = [];
    const currentDate = new Date();
    
    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      
      const cashFlow = calculateNetCashFlow(year, month);
      trends.push({
        year,
        month,
        monthName: date.toLocaleString('default', { month: 'long' }),
        ...cashFlow
      });
    }
    
    return trends;
  };

  const formatExpenseDate = (dateVal) => {
    if (!dateVal) return '—';
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  };

  const getOrdinal = (n) => {
    const v = n % 100;
    const suffix = (v >= 11 && v <= 13) ? 'th' : (v % 10 === 1 ? 'st' : v % 10 === 2 ? 'nd' : v % 10 === 3 ? 'rd' : 'th');
    return `${n}${suffix}`;
  };

  const getDueDateDisplay = (expense) => {
    if (expense.due_date) {
      const d = new Date(expense.due_date);
      if (!isNaN(d.getTime())) {
        if (expense.frequency === 'monthly') {
          return `${getOrdinal(d.getDate())} of month`;
        }
        return d.toLocaleDateString();
      }
    }
    if (expense.date) {
      const d = new Date(expense.date);
      if (!isNaN(d.getTime())) return d.toLocaleDateString();
    }
    return '—';
  };

  const getNextDueDate = (expense) => {
    const today = new Date();
    const rawDate = expense.due_date || expense.date;
    const dueDate = rawDate ? new Date(rawDate) : new Date(today.getFullYear(), today.getMonth(), 1);
    if (isNaN(dueDate.getTime())) return today;
    
    if (expense.frequency === 'monthly') {
      dueDate.setMonth(today.getMonth());
      if (dueDate < today) {
        dueDate.setMonth(today.getMonth() + 1);
      }
    } else if (expense.frequency === 'quarterly') {
      dueDate.setMonth(today.getMonth());
      if (dueDate < today) {
        dueDate.setMonth(today.getMonth() + 3);
      }
    } else if (expense.frequency === 'semi_annually') {
      dueDate.setMonth(today.getMonth());
      if (dueDate < today) {
        dueDate.setMonth(today.getMonth() + 6);
      }
    } else if (expense.frequency === 'yearly') {
      dueDate.setFullYear(today.getFullYear());
      if (dueDate < today) {
        dueDate.setFullYear(today.getFullYear() + 1);
      }
    }
    
    return dueDate;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading expense data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <BackToTaxFormsBanner />
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h1 className="text-3xl font-bold text-gray-900">Expense Tracker</h1>
            <Link
              to="/tax-forms"
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <FileText className="h-4 w-4" />
              View on Tax Forms
            </Link>
          </div>
          <p className="mt-2 text-gray-600">Track and manage your rental property expenses for tax optimization</p>
        </div>
        <Disclaimer variant="tax" />

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('expenses')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'expenses'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📊 Regular Expenses
              </button>
              <button
                onClick={() => setActiveTab('recurring')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'recurring'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🔄 Recurring Expenses & Cash Flow
              </button>
              <button
                onClick={() => setActiveTab('statements')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'statements'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🏦 Bank Statement Analysis
              </button>
              <button
                onClick={() => setActiveTab('cash-flow')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'cash-flow'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                💰 Cash Flow Dashboard
              </button>
              <button
                onClick={() => setActiveTab('tax-year')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'tax-year'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📊 Tax Year Tracker
              </button>
            </nav>
          </div>
        </div>

        {/* Regular Expenses Tab */}
        {activeTab === 'expenses' && (
          <>
                    {/* Summary Cards */}
        {(expenseSummary || expenses.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Expenses</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatCurrency(expenseSummary?.total_expenses || 0)}</p>
                  <p className="text-sm text-gray-500">Tax Year {expenseSummary?.tax_year ?? new Date().getFullYear()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Receipt className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Deductible</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatCurrency(expenseSummary?.deductible_expenses || 0)}</p>
                  <p className="text-sm text-gray-500">Tax savings</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Properties</p>
                  <p className="text-2xl font-semibold text-gray-900">{properties.length}</p>
                  <p className="text-sm text-gray-500">Active rentals</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <PiggyBank className="h-6 w-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Accounts</p>
                  <p className="text-2xl font-semibold text-gray-900">{accounts.length}</p>
                  <p className="text-sm text-gray-500">Bank & investment</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <button 
              onClick={() => setShowAddExpense(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </button>
            
            <button 
              onClick={() => setShowCSVImport(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </button>
            
            <button 
              onClick={async () => {
                if (!window.confirm('⚠️ This will permanently delete ALL expenses and imported income. Properties and accounts will NOT be affected. Start completely fresh?\n\nThis cannot be undone.')) return;
                try {
                  const res = await axios.post('/api/expenses/reset-all', {}, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
                  });
                  if (res.data.success) {
                    toast.success(res.data.message);
                    fetchExpenses();
                    fetchRecurringExpenses();
                    const auth = { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } };
                    const summaryRes = await axios.get(`/api/expenses/summary?tax_year=${filters.tax_year || new Date().getFullYear()}`, auth);
                    if (summaryRes.data?.success) setExpenseSummary(summaryRes.data.data);
                    else setExpenseSummary(null);
                  } else {
                    toast.error(res.data.error || 'Reset failed');
                  }
                } catch (e) {
                  toast.error('Failed to reset: ' + (e.response?.data?.message || e.message));
                }
              }}
              className="inline-flex items-center px-4 py-2 border border-red-200 text-sm font-medium rounded-md shadow-sm text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              title="Clear all expenses and imported income to start over"
            >
              Reset All
            </button>
            
            <select
              value={filters.property_id}
              onChange={(e) => setFilters({...filters, property_id: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Properties</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.name}
                </option>
              ))}
            </select>
            
            <select
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {getAlphabetizedCategories().map(([key, category]) => (
                <option key={key} value={key}>
                  {category.icon} {category.name}
                </option>
              ))}
            </select>

            <select
              value={filters.tax_year}
              onChange={(e) => setFilters({...filters, tax_year: e.target.value})}
              className="year-select-filter px-3 py-2 pr-8 min-w-[5.5rem] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Years</option>
              {getTaxYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
            </select>

            <select
              value={filters.is_deductible}
              onChange={(e) => setFilters({...filters, is_deductible: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Deductibility</option>
              <option value="true">Deductible Only</option>
              <option value="false">Non-Deductible Only</option>
            </select>
          </div>
        </div>

        {/* Expenses List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Expenses</h2>
            <p className="text-sm text-gray-600">
              Total: {formatCurrency(expenses.reduce((sum, e) => sum + e.amount, 0))} | 
              Count: {expenses.length}
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1100px' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deductible</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {expense.description}
                        {expense.is_recurring_display === 1 && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            🔄 Recurring
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="flex items-center">
                        {expenseCategories[expense.category]?.icon} 
                        {expenseCategories[expense.category]?.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getPropertyName(expense.property_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {expense.is_recurring_display === 1
                        ? (expense.due_date ? `${formatExpenseDate(expense.due_date)} (due)` : formatExpenseDate(expense.date))
                        : formatExpenseDate(expense.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        expense.is_deductible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {expense.is_deductible ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {expense.is_recurring_display === 1 ? (
                        <span className="text-xs text-gray-400 italic">Edit in Recurring tab</span>
                      ) : (
                        <div className="flex space-x-1 items-center">
                          <button
                            onClick={() => openEditExpense(expense)}
                            className="text-blue-600 hover:text-blue-900 px-1.5 py-0.5 rounded hover:bg-blue-50 flex items-center gap-0.5"
                            title="Edit expense"
                          >
                            <Edit className="h-3.5 w-3.5" />
                            <span className="text-xs">Edit</span>
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="text-red-600 hover:text-red-900 px-1.5 py-0.5 rounded hover:bg-red-50 flex items-center gap-0.5 border border-red-300"
                            title="Delete expense"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            <span className="text-xs">Del</span>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Expense Modal */}
        {showAddExpense && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Expense</h3>
                <form onSubmit={handleSubmitExpense(handleAddExpense)}>
                  <div className="space-y-4">
                    <ValidatedInput
                      name="description"
                      label="Description"
                      type="text"
                      register={registerExpense}
                      errors={expenseErrors}
                      placeholder="Enter expense description"
                      icon={Receipt}
                      required
                      {...registerExpense('description', {
                        required: 'Description is required',
                        minLength: {
                          value: 3,
                          message: 'Description must be at least 3 characters'
                        },
                        maxLength: {
                          value: 200,
                          message: 'Description must be no more than 200 characters'
                        }
                      })}
                    />

                    <ValidatedInput
                      name="amount"
                      label="Amount"
                      type="number"
                      register={registerExpense}
                      errors={expenseErrors}
                      placeholder="0.00"
                      icon={DollarSign}
                      required
                      step="0.01"
                      {...registerExpense('amount', {
                        required: 'Amount is required',
                        ...validationRules.currency,
                        validate: (value) => {
                          const num = parseFloat(value);
                          if (isNaN(num) || num <= 0) {
                            return 'Amount must be greater than 0';
                          }
                          return true;
                        }
                      })}
                    />

                    <ValidatedSelect
                      name="category"
                      label="Category"
                      register={registerExpense}
                      errors={expenseErrors}
                      placeholder="Select Category"
                      required
                      options={getAlphabetizedCategories().map(([key, category]) => ({
                        value: key,
                        label: `${category.icon} ${category.name}`
                      }))}
                      {...registerExpense('category', {
                        required: 'Category is required'
                      })}
                    />

                    <ValidatedSelect
                      name="property_id"
                      label="Property (Optional)"
                      register={registerExpense}
                      errors={expenseErrors}
                      placeholder="Select Property (Optional)"
                      options={[
                        { value: '', label: 'No Property' },
                        ...properties.map((property) => ({
                          value: property.id.toString(),
                          label: `${property.name} ${property.type === 'rental' ? '🏠' : '🏡'}`
                        }))
                      ]}
                      {...registerExpense('property_id')}
                    />

                    {watchedPropertyId && properties.find(p => p.id === parseInt(watchedPropertyId))?.type === 'rental' && (
                      <div className="text-xs text-purple-600 bg-purple-50 p-2 rounded">
                        💡 Auto-enabled tax deductible for rental property expenses
                      </div>
                    )}

                    <ValidatedSelect
                      name="account_id"
                      label="Account (Optional)"
                      register={registerExpense}
                      errors={expenseErrors}
                      placeholder="Select Account (Optional)"
                      options={[
                        { value: '', label: 'No Account' },
                        ...accounts.map((account) => ({
                          value: account.id.toString(),
                          label: account.name
                        }))
                      ]}
                      {...registerExpense('account_id')}
                    />

                    <ValidatedInput
                      name="date"
                      label="Date"
                      type="date"
                      register={registerExpense}
                      errors={expenseErrors}
                      icon={Calendar}
                      required
                      {...registerExpense('date', validationRules.date)}
                    />

                    <ValidatedSelect
                      name="tax_year"
                      label="Tax Year"
                      register={registerExpense}
                      errors={expenseErrors}
                      placeholder="Select Tax Year"
                      required
                      options={getTaxYearOptions().map(year => ({
                        value: year.toString(),
                        label: year.toString()
                      }))}
                      {...registerExpense('tax_year', {
                        required: 'Tax year is required'
                      })}
                    />

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_deductible"
                        {...registerExpense('is_deductible')}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="is_deductible" className="text-sm text-gray-700">
                        Tax Deductible
                      </label>
                    </div>

                    <ValidatedTextarea
                      name="notes"
                      label="Notes (Optional)"
                      register={registerExpense}
                      errors={expenseErrors}
                      placeholder="Additional notes about this expense"
                      rows={3}
                      {...registerExpense('notes', {
                        ...validationRules.maxLength(1000, 'Notes')
                      })}
                    />
                  </div>
                  <div className="flex space-x-3 mt-6">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700"
                    >
                      Add Expense
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddExpense(false);
                        resetExpense();
                      }}
                      className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Expense Modal */}
        {showEditExpense && selectedExpense && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Expense</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Description"
                    value={selectedExpense.description}
                    onChange={(e) => setSelectedExpense({...selectedExpense, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    placeholder="Amount"
                    value={selectedExpense.amount}
                    onChange={(e) => setSelectedExpense({...selectedExpense, amount: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={selectedExpense.category}
                    onChange={(e) => setSelectedExpense({...selectedExpense, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getAlphabetizedCategories().map(([key, category]) => (
                      <option key={key} value={key}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedExpense.property_id || ''}
                    onChange={(e) => setSelectedExpense({...selectedExpense, property_id: e.target.value ? parseInt(e.target.value) : null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No Property</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedExpense.account_id || ''}
                    onChange={(e) => setSelectedExpense({...selectedExpense, account_id: e.target.value ? parseInt(e.target.value) : null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No Account</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="date"
                    value={selectedExpense.date}
                    onChange={(e) => setSelectedExpense({...selectedExpense, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={selectedExpense.tax_year || new Date().getFullYear()}
                    onChange={(e) => setSelectedExpense({...selectedExpense, tax_year: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getTaxYearOptions().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <select
                    value={selectedExpense.expense_type || 'business'}
                    onChange={(e) => setSelectedExpense({...selectedExpense, expense_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="business">Business</option>
                    <option value="personal">Personal</option>
                    <option value="mixed">Mixed</option>
                  </select>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_is_deductible"
                      checked={selectedExpense.is_deductible}
                      onChange={(e) => setSelectedExpense({...selectedExpense, is_deductible: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="edit_is_deductible" className="text-sm text-gray-700">
                      Tax Deductible
                    </label>
                  </div>
                  <textarea
                    placeholder="Notes (optional)"
                    value={selectedExpense.notes || ''}
                    onChange={(e) => setSelectedExpense({...selectedExpense, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={handleEditExpense}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700"
                  >
                    Update Expense
                  </button>
                  <button
                    onClick={() => setShowEditExpense(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
          </>
        )}

        {/* Quick Navigation for Main Expenses Tab */}
        {activeTab === 'expenses' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🚀 Quick Navigation</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">📊 Tax Year Overview</h4>
                <p className="text-blue-700 text-sm mb-3">View annual rental income, deductions, and savings planning</p>
                <button
                  onClick={() => setActiveTab('tax-year')}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                >
                  Go to Tax Year Tracker →
                </button>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">🏦 Bank Statement Analysis</h4>
                <p className="text-green-700 text-sm mb-3">Upload and analyze bank statements for automatic categorization</p>
                <button
                  onClick={() => setActiveTab('statements')}
                  className="text-green-600 hover:text-green-800 font-medium text-sm"
                >
                  Go to Statement Analysis →
                </button>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-900 mb-2">🔄 Recurring Expenses</h4>
                <p className="text-orange-700 text-sm mb-3">Manage monthly recurring bills and cash flow</p>
                <button
                  onClick={() => setActiveTab('recurring')}
                  className="text-orange-600 hover:text-orange-800 font-medium text-sm"
                >
                  Go to Recurring Expenses →
                </button>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-2">💰 Cash Flow Dashboard</h4>
                <p className="text-purple-700 text-sm mb-3">Monitor monthly spending vs income with trends</p>
                <button
                  onClick={() => setActiveTab('cash-flow')}
                  className="text-purple-600 hover:text-purple-800 font-medium text-sm"
                >
                  Go to Cash Flow Dashboard →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recurring Expenses Tab */}
        {activeTab === 'recurring' && (
          <div className="space-y-6">
            {/* Cash Flow Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Monthly Rent Income</p>
                    <p className="text-2xl font-semibold text-gray-900">{formatCurrency(monthlyRentIncome)}</p>
                    <p className="text-xs text-gray-500">Auto-calculated from properties</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Receipt className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Monthly Expenses</p>
                    <p className="text-2xl font-semibold text-gray-900">{formatCurrency(calculateMonthlyExpenses())}</p>
                    <p className="text-xs text-gray-500">Including monthly portions</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <PiggyBank className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Monthly Savings Needed</p>
                    <p className="text-2xl font-semibold text-gray-900">{formatCurrency(calculateMonthlySavings())}</p>
                    <p className="text-xs text-gray-500">For Marcus account</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Net Monthly Cash Flow</p>
                    <p className={`text-2xl font-semibold ${calculateNetMonthlyCashFlow() >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(calculateNetMonthlyCashFlow())}
                    </p>
                    <p className="text-xs text-gray-500">Income - Expenses</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recurring Expenses Controls */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recurring Expenses</h2>
                <button
                  onClick={() => setShowAddRecurring(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Recurring Expense
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date / Due Day</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Due</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Portion</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recurringExpenses.map((expense) => (
                      <tr key={expense.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {expense.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(expense.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            expense.frequency === 'monthly' ? 'bg-blue-100 text-blue-800' :
                            expense.frequency === 'quarterly' ? 'bg-green-100 text-green-800' :
                            expense.frequency === 'semi_annually' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {expense.frequency === 'monthly' ? 'Monthly' :
                             expense.frequency === 'quarterly' ? 'Quarterly' :
                             expense.frequency === 'semi_annually' ? 'Semi-Annually' : 'Yearly'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getDueDateDisplay(expense)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getNextDueDate(expense).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCurrency(
                            expense.frequency === 'monthly' ? expense.amount :
                            expense.frequency === 'quarterly' ? expense.amount / 3 :
                            expense.frequency === 'semi_annually' ? expense.amount / 6 :
                            expense.amount / 12
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getPropertyName(expense.property_id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setSelectedRecurring(expense);
                                setShowEditRecurring(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteRecurringExpense(expense.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Add Recurring Expense Modal */}
        {showAddRecurring && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add Recurring Expense</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Description"
                    value={newRecurringExpense.description}
                    onChange={(e) => setNewRecurringExpense({...newRecurringExpense, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    placeholder="Amount"
                    value={newRecurringExpense.amount}
                    onChange={(e) => setNewRecurringExpense({...newRecurringExpense, amount: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={newRecurringExpense.category}
                    onChange={(e) => setNewRecurringExpense({...newRecurringExpense, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Category</option>
                    {getAlphabetizedCategories().map(([key, category]) => (
                      <option key={key} value={key}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                  <select
                    name="frequency"
                    value={newRecurringExpense.frequency}
                    onChange={(e) => setNewRecurringExpense({...newRecurringExpense, frequency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monthly">📅 Monthly</option>
                    <option value="quarterly">📆 Quarterly</option>
                    <option value="semi_annually">📈 Semi-Annually</option>
                    <option value="yearly">🗓️ Yearly</option>
                  </select>
                  <select
                    value={newRecurringExpense.property_id}
                    onChange={(e) => {
                      const propertyId = e.target.value ? parseInt(e.target.value) : null;
                      const selectedProperty = properties.find(p => p.id === propertyId);
                      // Auto-set tax deductible based on property type for recurring expenses
                      const isDeductible = selectedProperty?.type === 'rental' ? true : newRecurringExpense.is_deductible;
                      setNewRecurringExpense({...newRecurringExpense, property_id: propertyId, is_deductible: isDeductible});
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Property (Required)</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.name} {property.type === 'rental' ? '🏠' : '🏡'} - {property.address}
                      </option>
                    ))}
                  </select>
                  {newRecurringExpense.property_id && properties.find(p => p.id === newRecurringExpense.property_id)?.type === 'rental' && (
                    <div className="text-xs text-purple-600 bg-purple-50 p-2 rounded">
                      💡 Auto-enabled tax deductible for rental property recurring expenses
                    </div>
                  )}
                  <select
                    value={newRecurringExpense.bill_type}
                    onChange={(e) => setNewRecurringExpense({...newRecurringExpense, bill_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Bill Type (Required)</option>
                    {Object.entries(billTypes).map(([key, billType]) => (
                      <option key={key} value={key}>
                        {billType.icon} {billType.name}
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={newRecurringExpense.due_date}
                        onChange={(e) => setNewRecurringExpense({...newRecurringExpense, due_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <select
                    value={newRecurringExpense.tax_year}
                    onChange={(e) => setNewRecurringExpense({...newRecurringExpense, tax_year: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getTaxYearOptions().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_deductible_recurring"
                      checked={newRecurringExpense.is_deductible}
                      onChange={(e) => setNewRecurringExpense({...newRecurringExpense, is_deductible: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="is_deductible_recurring" className="text-sm text-gray-700">
                      Tax Deductible
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="auto_pay"
                      checked={newRecurringExpense.auto_pay}
                      onChange={(e) => setNewRecurringExpense({...newRecurringExpense, auto_pay: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="auto_pay" className="text-sm text-gray-700">
                      Auto-Pay Enabled
                    </label>
                  </div>
                  <textarea
                    placeholder="Notes (optional)"
                    value={newRecurringExpense.notes}
                    onChange={(e) => setNewRecurringExpense({...newRecurringExpense, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={handleAddRecurringExpense}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700"
                  >
                    Add Recurring Expense
                  </button>
                  <button
                    onClick={() => setShowAddRecurring(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Recurring Expense Modal */}
        {showEditRecurring && selectedRecurring && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Recurring Expense</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Description"
                    value={selectedRecurring.description}
                    onChange={(e) => setSelectedRecurring({...selectedRecurring, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    placeholder="Amount"
                    value={selectedRecurring.amount}
                    onChange={(e) => setSelectedRecurring({...selectedRecurring, amount: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={selectedRecurring.category}
                    onChange={(e) => setSelectedRecurring({...selectedRecurring, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getAlphabetizedCategories().map(([key, category]) => (
                      <option key={key} value={key}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedRecurring.frequency}
                    onChange={(e) => setSelectedRecurring({...selectedRecurring, frequency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="semi_annually">Semi-Annually</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <select
                    value={selectedRecurring.property_id || ''}
                    onChange={(e) => setSelectedRecurring({...selectedRecurring, property_id: e.target.value ? parseInt(e.target.value) : null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No Property</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="date"
                    value={selectedRecurring.due_date}
                    onChange={(e) => setSelectedRecurring({...selectedRecurring, due_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_is_deductible_recurring"
                      checked={selectedRecurring.is_deductible}
                      onChange={(e) => setSelectedRecurring({...selectedRecurring, is_deductible: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="edit_is_deductible_recurring" className="text-sm text-gray-700">
                      Tax Deductible
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_auto_pay"
                      checked={selectedRecurring.auto_pay}
                      onChange={(e) => setSelectedRecurring({...selectedRecurring, auto_pay: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="edit_auto_pay" className="text-sm text-gray-700">
                      Auto-Pay Enabled
                    </label>
                  </div>
                  <textarea
                    placeholder="Notes (optional)"
                    value={selectedRecurring.notes || ''}
                    onChange={(e) => setSelectedRecurring({...selectedRecurring, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={handleEditRecurringExpense}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700"
                  >
                    Update Recurring Expense
                  </button>
                  <button
                    onClick={() => setShowEditRecurring(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Navigation for Recurring Expenses Tab */}
        {activeTab === 'recurring' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🚀 Quick Navigation</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">📊 Tax Year Overview</h4>
                <p className="text-blue-700 text-sm mb-3">View annual rental income, deductions, and savings planning</p>
                <button
                  onClick={() => setActiveTab('tax-year')}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                >
                  Go to Tax Year Tracker →
                </button>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">🏦 Bank Statement Analysis</h4>
                <p className="text-green-700 text-sm mb-3">Upload and analyze bank statements for automatic categorization</p>
                <button
                  onClick={() => setActiveTab('statements')}
                  className="text-green-600 hover:text-green-800 font-medium text-sm"
                >
                  Go to Statement Analysis →
                </button>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-2">📝 Manual Expenses</h4>
                <p className="text-purple-700 text-sm mb-3">Add and manage individual expenses manually</p>
                <button
                  onClick={() => setActiveTab('expenses')}
                  className="text-purple-600 hover:text-purple-800 font-medium text-sm"
                >
                  Go to Expense Tracker →
                </button>
              </div>
              
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-900 mb-2">💰 Cash Flow Dashboard</h4>
                <p className="text-indigo-700 text-sm mb-3">Monitor monthly spending vs income with trends</p>
                <button
                  onClick={() => setActiveTab('cash-flow')}
                  className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                >
                  Go to Cash Flow Dashboard →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Test Tab - Bank Statement Analysis */}
        {activeTab === 'statements' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">🏦 Bank Statement Analysis</h2>
                <button
                  onClick={() => setShowBankAnalyzer(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Analyze Bank Statements
                </button>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-800 mb-2">📋 Analysis Focus</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>PDF & CSV Support</strong> - Handles USAA PDF statements and CSV exports</li>
                  <li>• <strong>Costa Rica transactions</strong> - Identify personal expenses in Costa Rica</li>
                  <li>• <strong>Rental income patterns</strong> - Detect consistent monthly income vs new income</li>
                  <li>• <strong>Recurring expenses</strong> - Find monthly bills and subscriptions</li>
                  <li>• <strong>New ongoing bills</strong> - Identify new monthly expenses (like $45 internet)</li>
                  <li>• <strong>Expense categorization</strong> - Auto-categorize transactions</li>
                </ul>
              </div>

              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">📊 Expected Results</h3>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• <strong>Rental Income:</strong> Consistent $4,300/month + new $4,300 this month</li>
                  <li>• <strong>Costa Rica Expenses:</strong> Personal living expenses in Costa Rica</li>
                  <li>• <strong>Recurring Bills:</strong> Monthly utilities, subscriptions, etc.</li>
                  <li>• <strong>New Bills:</strong> $45 internet bill (new this month)</li>
                </ul>
              </div>

              {/* Quick Navigation */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">📊 Tax Year Overview</h3>
                  <p className="text-blue-700 text-sm mb-3">View annual rental income, deductions, and savings planning</p>
                  <button
                    onClick={() => setActiveTab('tax-year')}
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                  >
                    Go to Tax Year Tracker →
                  </button>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-2">🔄 Recurring Expenses</h3>
                  <p className="text-green-700 text-sm mb-3">Manage monthly recurring bills and cash flow</p>
                  <button
                    onClick={() => setActiveTab('recurring')}
                    className="text-green-600 hover:text-green-800 font-medium text-sm"
                  >
                    Go to Recurring Expenses →
                  </button>
                </div>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="font-semibold text-purple-900 mb-2">📝 Manual Expenses</h3>
                  <p className="text-purple-700 text-sm mb-3">Add and manage individual expenses manually</p>
                  <button
                    onClick={() => setActiveTab('expenses')}
                    className="text-purple-600 hover:text-purple-800 font-medium text-sm"
                  >
                    Go to Expense Tracker →
                  </button>
                </div>
                
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h3 className="font-semibold text-indigo-900 mb-2">💰 Cash Flow Dashboard</h3>
                  <p className="text-indigo-700 text-sm mb-3">Monitor monthly spending vs income with trends</p>
                  <button
                    onClick={() => setActiveTab('cash-flow')}
                    className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                  >
                    Go to Cash Flow Dashboard →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tax Year Tracker Tab */}
        {activeTab === 'tax-year' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">📊 Tax Year Tracker</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab('statements')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Analyze Statements
                  </button>
                  <button
                    onClick={() => setActiveTab('recurring')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Calculator className="h-4 w-4 mr-2" />
                    Recurring Expenses
                  </button>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Track rental income by month, annual deductions by property, and plan your Marcus savings for quarterly/semi-annual expenses.
              </p>
            </div>
            
            {/* Tax Year Tracker Component */}
            <TaxYearTracker />
          </div>
        )}

        {/* Cash Flow Dashboard Tab */}
        {activeTab === 'cash-flow' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">💰 Cash Flow Dashboard</h2>
                <div className="flex gap-2">
                  <select
                    value={cashFlowData.selectedYear ?? new Date().getFullYear()}
                    onChange={(e) => {
                      const year = parseInt(e.target.value);
                      const monthPart = (cashFlowData.selectedMonth || '').split('-')[1] || String(new Date().getMonth() + 1).padStart(2, '0');
                      setCashFlowData({...cashFlowData, selectedYear: year, selectedMonth: `${year}-${monthPart}`});
                    }}
                    className="year-select-filter px-3 py-2 pr-8 min-w-[5.5rem] border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getTaxYearOptions().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <select
                    value={cashFlowData.selectedMonth}
                    onChange={(e) => setCashFlowData({...cashFlowData, selectedMonth: e.target.value})}
                    className="year-select-filter px-3 py-2 pr-8 min-w-[9rem] border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({length: 12}, (_, i) => {
                      const year = parseInt(cashFlowData.selectedYear || new Date().getFullYear());
                      const date = new Date(year, i, 1);
                      const value = `${year}-${String(i + 1).padStart(2, '0')}`;
                      const label = date.toLocaleString('default', { month: 'long' });
                      return <option key={value} value={value}>{label}</option>;
                    })}
                  </select>
                  <button
                    onClick={() => setActiveTab('statements')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Analyze Statements
                  </button>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Monitor your monthly spending vs income with detailed breakdowns and trends.
              </p>
            </div>

            {/* Current Month Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {(() => {
                const [year, month] = cashFlowData.selectedMonth.split('-').map(Number);
                const currentMonthData = calculateNetCashFlow(year, month);
                return (
                  <>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <DollarSign className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Monthly Income</p>
                          <p className="text-2xl font-semibold text-gray-900">{formatCurrency(currentMonthData.income)}</p>
                          <p className="text-xs text-gray-500">Rental + Other Income</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <Receipt className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Monthly Expenses</p>
                          <p className="text-2xl font-semibold text-gray-900">{formatCurrency(currentMonthData.expenses)}</p>
                          <p className="text-xs text-gray-500">All Categories</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <PiggyBank className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Monthly Savings</p>
                          <p className="text-2xl font-semibold text-gray-900">{formatCurrency(currentMonthData.savings)}</p>
                          <p className="text-xs text-gray-500">To Marcus & Other</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <TrendingUp className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Net Cash Flow</p>
                          <p className={`text-2xl font-semibold ${currentMonthData.netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(currentMonthData.netFlow)}
                          </p>
                          <p className="text-xs text-gray-500">Income - Expenses - Savings</p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Expense Categories Breakdown */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Expense Categories Breakdown</h3>
              {(() => {
                const [year, month] = cashFlowData.selectedMonth.split('-').map(Number);
                const categories = calculateMonthlyExpensesByCategory(year, month);
                const totalExpenses = Object.values(categories).reduce((sum, amount) => sum + amount, 0);
                
                return (
                  <div className="space-y-3">
                    {Object.entries(categories).map(([category, amount]) => {
                      const percentage = totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0;
                      return (
                        <div key={category} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                            <span className="text-sm font-medium text-gray-700 capitalize">
                              {category.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-semibold text-gray-900">{formatCurrency(amount)}</span>
                            <span className="text-xs text-gray-500 ml-2">({percentage.toFixed(1)}%)</span>
                          </div>
                        </div>
                      );
                    })}
                    {Object.keys(categories).length === 0 && (
                      <p className="text-gray-500 text-center py-4">No expenses recorded for this month</p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Monthly Trends */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 12-Month Cash Flow Trends</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Income</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expenses</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Savings</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Flow</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getMonthlyTrends().map((trend) => (
                      <tr key={`${trend.year}-${trend.month}`} className={trend.month === parseInt(cashFlowData.selectedMonth.split('-')[1]) ? 'bg-blue-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {trend.monthName} {trend.year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(trend.income)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(trend.expenses)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(trend.savings)}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${trend.netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(trend.netFlow)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick Navigation */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🚀 Quick Navigation</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">📊 Tax Year Overview</h4>
                  <p className="text-blue-700 text-sm mb-3">View annual rental income, deductions, and savings planning</p>
                  <button
                    onClick={() => setActiveTab('tax-year')}
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                  >
                    Go to Tax Year Tracker →
                  </button>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">🏦 Bank Statement Analysis</h4>
                  <p className="text-green-700 text-sm mb-3">Upload and analyze bank statements for automatic categorization</p>
                  <button
                    onClick={() => setActiveTab('statements')}
                    className="text-green-600 hover:text-green-800 font-medium text-sm"
                  >
                    Go to Statement Analysis →
                  </button>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-900 mb-2">🔄 Recurring Expenses</h4>
                  <p className="text-orange-700 text-sm mb-3">Manage monthly recurring bills and cash flow</p>
                  <button
                    onClick={() => setActiveTab('recurring')}
                    className="text-orange-600 hover:text-orange-800 font-medium text-sm"
                  >
                    Go to Recurring Expenses →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CSV Import Modal */}
        {showCSVImport && (
          <CSVImport 
            onImportComplete={() => {
              fetchExpenses();
              setShowCSVImport(false);
            }}
            onClose={() => setShowCSVImport(false)}
          />
        )}

        {/* Bank Statement Analyzer Modal */}
        {showBankAnalyzer && (
          <BankStatementAnalyzer 
            onClose={() => setShowBankAnalyzer(false)}
            onTransactionsCategorized={(data) => {
              if (data.category === 'merge') {
                fetchExpenses();
                setActiveTab('recurring');
                return;
              }
              if (data.category !== 'batch_save') {
                toast.success(`Successfully categorized ${data.count} transactions as ${data.category}`);
              }
              fetchExpenses();
              if (data.category === 'rental_income') fetchData();
              if (data.category === 'recurring' || data.category === 'new_bill') setActiveTab('recurring');
              else if (data.category === 'rental_income') setActiveTab('cash-flow');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ExpenseTracker;
